import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, TrendingUp, Zap } from "lucide-react";

// Rotate through a list every `intervalMs`. Pauses on hover so a user
// about to click never has the card swap out from under their cursor.
// Resets to index 0 whenever the list identity changes (e.g. a new
// trade reshuffles the top 5) so we don't index past the end.
function useRotatingPick(items, intervalMs = 11000) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => { setIdx(0); }, [items]);
  useEffect(() => {
    if (paused || items.length <= 1) return undefined;
    const t = setInterval(
      () => setIdx((i) => (i + 1) % items.length),
      intervalMs
    );
    return () => clearInterval(t);
  }, [paused, items.length, intervalMs]);
  const safeIdx = items.length === 0 ? 0 : idx % items.length;
  return {
    current: items[safeIdx] ?? null,
    pause: () => setPaused(true),
    resume: () => setPaused(false),
    idx: safeIdx,
    total: items.length,
  };
}

// Wraps a featured card and crossfades its content when the token id
// changes. Single rendered card at any time — fade-out → swap → fade-in
// over 200ms each. Cleaner than mounting two cards and tracking the
// outgoing one's exit animation.
function RotatingCardSlot({ token, render, onMouseEnter, onMouseLeave }) {
  const [display, setDisplay] = useState(token);
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const newKey = token?.contractAddress;
    const oldKey = display?.contractAddress;
    if (newKey === oldKey) return undefined;
    setFading(true);
    const t = setTimeout(() => {
      setDisplay(token);
      setFading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [token, display]);
  return (
    <div
      className={`pm-trend__rotator${fading ? " is-fading" : ""}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {render(display)}
    </div>
  );
}

const fmtUsd = (n) => {
  if (n == null || Number.isNaN(+n)) return "—";
  const v = +n;
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${Math.round(v)}`;
};

const fmtPct = (n) => {
  if (n == null) return "—";
  const v = +n;
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
};

const ageSecondsOf = (t) => {
  if (typeof t?.age_seconds === "number") return t.age_seconds;
  const ts = t?.createdAt || t?.created_at;
  if (!ts) return null;
  const ms = Date.now() - new Date(ts).getTime();
  return Math.max(0, Math.round(ms / 1000));
};

const fmtAge = (secs) => {
  if (secs == null || !Number.isFinite(secs)) return "—";
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

function FeaturedCard({ kicker, icon: Icon, token, metric, metricSub }) {
  const navigate = useNavigate();
  if (!token) return null;
  return (
    <button
      type="button"
      className="pm-trend__card"
      onClick={() => navigate(`/token/${token.contractAddress}`)}
    >
      <span className="pm-trend__metric-label">
        <Icon className="pm-trend__metric-icon" />
        {kicker}
      </span>
      <div className="pm-trend__body">
        <div className="pm-trend__avatar" data-ticker={token?.symbol ? `$${token.symbol}` : ""}>
          {token.image?.url ? <img src={token.image.url} alt={token.name} /> : null}
        </div>
        <div className="pm-trend__info">
          <div className="pm-trend__id">
            <div className="pm-trend__name">{token.name}</div>
            <div className="pm-trend__mc">
              <span className="pm-trend__mc-value">{fmtUsd(token.market_cap_usd)}</span>
            </div>
          </div>
        </div>
      </div>
      {token?.fee_mode === "burn" && (
        <div
          className="pm-trend__burn-sticky"
          title="Fees are bought back + burnt forever"
          aria-label="Fees are burnt"
        >
          <span className="pm-trend__burn-sticky-label">Fees are</span>
          <span className="pm-trend__burn-sticky-amount">burnt!</span>
        </div>
      )}
      {token?.fee_mode === "lp_compound" && (
        <div
          className="pm-trend__lp-sticky"
          title="Fees auto-compound back into the LP — pool grows forever"
          aria-label="Fees go to LP"
        >
          <span className="pm-trend__burn-sticky-label">Fees</span>
          <span className="pm-trend__burn-sticky-amount">grow LP!</span>
        </div>
      )}
      <div className="pm-trend__metric">
        <span className="pm-trend__metric-value">
          <span className="pm-trend__metric-num">{metric}</span>
          <span className="pm-trend__metric-sub">{metricSub}</span>
        </span>
      </div>
    </button>
  );
}

export default function FeaturedTokens({ tokens }) {
  // Compute top 5 for the two rotating categories. Newest stays single
  // and gets replaced naturally when a fresh launch enters the list.
  const { topVolumeList, topGainerList, newest } = useMemo(() => {
    const list = Array.isArray(tokens) ? tokens : [];
    const byVol = [...list]
      .sort((a, b) => (b.volume_usd_24h || 0) - (a.volume_usd_24h || 0))
      .slice(0, 5);
    const byGain = [...list]
      .sort((a, b) => (b.price_change_24h || 0) - (a.price_change_24h || 0))
      .slice(0, 5);
    const byAge = [...list].sort(
      (a, b) => (ageSecondsOf(a) ?? Infinity) - (ageSecondsOf(b) ?? Infinity)
    );
    return { topVolumeList: byVol, topGainerList: byGain, newest: byAge[0] };
  }, [tokens]);

  // Two staggered intervals (11s + 13s) so the cards don't crossfade in
  // lockstep — feels more organic than synchronized swapping.
  const vol = useRotatingPick(topVolumeList, 11000);
  const gain = useRotatingPick(topGainerList, 13000);

  if (!vol.current && !gain.current && !newest) return null;

  return (
    <section className="pm-trend">
      <div className="pm-section-heading pm-section-heading--feature">
        <div className="pm-section-heading__group">
          <span className="pm-section-heading__sub">
            <Flame className="pm-section-heading__sub-icon" />
            Top 3 · 24h
          </span>
          <h2>Featured tokens</h2>
        </div>
      </div>
      <div className="pm-trend__grid">
        <RotatingCardSlot
          token={vol.current}
          onMouseEnter={vol.pause}
          onMouseLeave={vol.resume}
          render={(t) => (
            <FeaturedCard
              kicker="Top volume · 24h"
              icon={Flame}
              token={t}
              metric={fmtUsd(t?.volume_usd_24h)}
              metricSub="24h vol"
            />
          )}
        />
        <RotatingCardSlot
          token={gain.current}
          onMouseEnter={gain.pause}
          onMouseLeave={gain.resume}
          render={(t) => (
            <FeaturedCard
              kicker="Top gainer · 24h"
              icon={TrendingUp}
              token={t}
              metric={fmtPct(t?.price_change_24h)}
              metricSub="24h chg"
            />
          )}
        />
        <FeaturedCard
          kicker="Newest launch"
          icon={Zap}
          token={newest}
          metric={fmtAge(ageSecondsOf(newest))}
          metricSub="age"
        />
      </div>
    </section>
  );
}
